import * as v from "valibot";

/**
 * Create a Valibot schema for Dynamo keys.
 * Pass in key names in the correct order.
 * @example
 * ```ts
 * const QuestionSk = compoundKey("URLID", "QUESTION");
 * ````
 * To encode a key to a string for saving in Dynamo use `.encode`:
 * @example
 * ```ts
 * QuestionSk.encode({ URLID: "abc", QUESTION: "saq1"});
 * // "URLID#abc~QUESTION#saq1"
 * ````
 * To decode a key from a Dynamo string parse with Valibot:
 * @example
 * ```ts
 * v.parse(QuestionSk, "URLID#123~QUESTION#saq1");
 * // { URLID: "123", QUESTION: "saq1" }
 * ```
 * To encode a partial key (e.g. for `beginsWith` queries) use `.partial`:
 * @example
 * ```
 * QuestionSk.partial({ URLID: "abc" });
 * // "URLID#abc~QUESTION#"
 */
export function compoundKey<const Names extends string[]>(...names: Names) {
  type Decoded = { [K in Names[number]]: string } & {};

  const schema = v.pipe(
    v.string(),
    // When parsed with Valibot, decode string into object
    v.transform((raw) => {
      const entries = raw.split("~").map((s) => {
        const [key, val] = s.split("#");
        return [key, val ?? null];
      });
      return Object.fromEntries(entries) as Decoded;
    }),
  );

  return Object.assign(schema, {
    encode(values: Decoded): Encoded<Names> {
      return names
        .map((name: Names[number]) => {
          if (!values[name]) {
            throw new Error(`"${name}" must be a non-empty string`);
          }
          return name + "#" + values[name];
        })
        .join("~") as Encoded<Names>;
    },

    partial(values?: Partial<Decoded>): string {
      return (names as Names[number][])
        .filter((name) => values?.[name] !== undefined)
        .map((name) => name + "#" + values![name])
        .join("~");
    },
  });
}

/**
 * Utility to get nice template literal types instead of `string`
 * It recursively extracts segments into `key#value~key#value`
 */
type Encoded<P extends readonly string[]> = P extends readonly [
  infer Only extends string,
]
  ? `${Only}#${string}`
  : P extends readonly [
        infer First extends string,
        ...infer Rest extends readonly string[],
      ]
    ? `${First}#${string}~${Encoded<Rest>}`
    : string;
